import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Dialog, FormField, Input, Text, Textarea, cn } from '@beatq/ui';
import { usePermissions } from '@beatq/shared';
import {
  filterAuthorisedHits,
  groupSearchHits,
  hrefForSearchHit,
  matchFrequentActions,
  PURPOSE_JUSTIFICATION_MIN,
  searchAdmin,
  subjectNeedsPurposeBinding,
  type AdminSearchHit,
  type PaletteAction,
} from './adminSearchApi.js';
import { QueueList, QueueListRow } from './queueList.js';
import { AdminIcon, Search } from './adminIcons.js';
import { parseSubjectFromLocation } from './subjectUrls.js';
import { setStoredAccessReason } from './adminSession.js';

const TYPE_LABELS: Record<string, string> = {
  case: 'Cases',
  order: 'Orders',
  customer: 'Customers',
  business: 'Businesses',
  branch: 'Branches',
  payment: 'Payments',
  audit: 'Audit',
  subject: 'Subjects',
};

export function AdminCommandPalette() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { hasPerm, loading: permsLoading, authenticated } = usePermissions();
  const permsReady = authenticated && !permsLoading;
  const openCaseId = parseSubjectFromLocation(location.pathname, searchParams).caseId;

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<AdminSearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingHit, setPendingHit] = useState<AdminSearchHit | null>(null);
  const [justification, setJustification] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === '/' && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setHits([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      void searchAdmin(trimmed)
        .then((rows) => {
          setHits(filterAuthorisedHits(rows, hasPerm, permsReady));
          setError(null);
        })
        .catch((err: unknown) => {
          setHits([]);
          setError(err instanceof Error ? err.message : 'Search failed');
        })
        .finally(() => setLoading(false));
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q, open, hasPerm, permsReady]);

  function close() {
    setOpen(false);
    setQ('');
    setHits([]);
    setError(null);
    setPendingHit(null);
    setJustification('');
  }

  function goHit(hit: AdminSearchHit) {
    if (subjectNeedsPurposeBinding(hit, openCaseId)) {
      setPendingHit(hit);
      return;
    }
    navigate(hrefForSearchHit(hit, openCaseId));
    close();
  }

  function confirmPurpose() {
    if (!pendingHit) return;
    const reason = justification.trim();
    if (reason.length < PURPOSE_JUSTIFICATION_MIN) return;
    setStoredAccessReason(reason);
    navigate(hrefForSearchHit(pendingHit, openCaseId));
    close();
  }

  function goAction(action: PaletteAction) {
    navigate(action.href);
    close();
  }

  const groups = groupSearchHits(hits, q);
  const actions = matchFrequentActions(q, hasPerm, permsReady);

  const modKey =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label="Search subjects"
        className={cn(
          'flex min-h-11 w-full max-w-md items-center gap-inline rounded-control border border-line bg-page px-field text-left',
          'text-body-sm text-muted shadow-card hov:border-line-strong hov:bg-raised',
        )}
      >
        <AdminIcon icon={Search} size={16} />
        <span className="flex-1 truncate">Search orders, cases, users, businesses…</span>
        <kbd className="rounded-control border border-line bg-raised px-xs py-xs text-caption font-medium text-muted">
          {modKey}K
        </kbd>
      </button>
      <Dialog
        open={open}
        onClose={close}
        title="Search subjects"
        description="Find by order ref, case ref, email, telephone, business or branch name, postcode, payment ref, or audit ref. Results respect your permissions."
        loading={loading}
        error={error ?? undefined}
        footer={
          <Button type="button" variant="secondary" onClick={close}>
            Close
          </Button>
        }
      >
        <div className="relative">
          <span className="pointer-events-none absolute left-field top-1/2 -translate-y-1/2 text-muted">
            <AdminIcon icon={Search} size={16} />
          </span>
          <Input
            id="admin-palette-q"
            label="Query"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="BQ-4K2M, SUP-88213, email, postcode…"
            autoComplete="off"
            autoFocus
            className="pl-10"
          />
        </div>
        {actions.length > 0 ? (
          <div className="mt-field">
            <Text variant="caption" className="mb-xs text-muted">
              Actions
            </Text>
            <QueueList label="Frequent actions">
              {actions.map((action) => (
                <QueueListRow
                  key={action.id}
                  title={action.name}
                  actionLabel={action.name}
                  onActivate={() => goAction(action)}
                />
              ))}
            </QueueList>
          </div>
        ) : null}
        {!loading && !error && q.trim().length >= 2 && groups.length === 0 ? (
          <p role="status">No matches.</p>
        ) : null}
        <div role="listbox" aria-label="Search results">
          {groups.map((group) => (
            <div key={group.type} className="mt-field">
              <Text variant="caption" className="mb-xs text-muted">
                {TYPE_LABELS[group.type] ?? group.type}
              </Text>
              <QueueList label={TYPE_LABELS[group.type] ?? group.type}>
                {group.items.map((hit) => (
                  <QueueListRow
                    key={`${hit.type}-${hit.id}`}
                    title={hit.name}
                    meta={hit.id !== hit.name ? hit.id : undefined}
                    actionLabel={`Open ${hit.name}`}
                    onActivate={() => goHit(hit)}
                  />
                ))}
              </QueueList>
            </div>
          ))}
        </div>
      </Dialog>
      <Dialog
        open={Boolean(pendingHit)}
        onClose={() => {
          setPendingHit(null);
          setJustification('');
        }}
        title="Purpose for this access"
        description="Opening a subject with no open case requires a typed justification. This is recorded with the read."
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setPendingHit(null);
                setJustification('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmPurpose}
              disabled={justification.trim().length < PURPOSE_JUSTIFICATION_MIN}
            >
              Continue
            </Button>
          </>
        }
      >
        {pendingHit ? (
          <p className="mb-field text-body-sm">
            {pendingHit.name} ({pendingHit.type} {pendingHit.id})
          </p>
        ) : null}
        <FormField
          label="Justification"
          htmlFor="admin-palette-purpose"
          required
          hint={`At least ${PURPOSE_JUSTIFICATION_MIN} characters.`}
        >
          <Textarea
            id="admin-palette-purpose"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            rows={3}
          />
        </FormField>
      </Dialog>
    </>
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}
